'use client'

import dayjs from 'dayjs'
import { useParams, getTranslations } from '@/lib/next-shim'
import { OrderStatus, GetTrackedOrderQuery } from '@/core/graphql/types'
import { useGetTrackedOrderQuery } from '@/core/graphql/hooks'
import { Step } from './Step'

const returnActiveStepFromStatus = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.Pending:
      return 1
    case OrderStatus.Accepted:
    case OrderStatus.Refused:
    case OrderStatus.Canceled:
    case OrderStatus.ConsultationCreated:
      return 2
    default:
      return 1
  }
}

export function OrderTrackingPage() {
  const params = useParams()
  const referenceId = params?.referenceId as string
  const t = getTranslations()

  const { data, loading, error } = useGetTrackedOrderQuery({
    variables: { referenceId },
    skip: !referenceId,
  })

  if (loading) return <div className="p-10 text-center">{t('common.loading')}</div>
  if (error) return <div className="p-10 text-center text-red-500">{t('common.error', { message: error.message })}</div>

  const trackedOrder = data?.trackedOrder

  if (!trackedOrder) return <div className="p-10 text-center">{t('common.orderNotFound')}</div>

  const returnSteps = (order: NonNullable<GetTrackedOrderQuery['trackedOrder']>) => {
    const activeStep = returnActiveStepFromStatus(order.status)
    return [
      {
        id: 1,
        label: t('model.order.status.Created'),
        status: (activeStep >= 1 ? 'complete' : 'current') as 'complete' | 'current' | 'upcoming',
      },
      {
        id: 2,
        label:
          order.status === OrderStatus.Accepted || order.status === OrderStatus.ConsultationCreated
            ? t('model.order.status.Accepted')
            : order.status === OrderStatus.Canceled
              ? t('model.order.status.Canceled')
              : order.status === OrderStatus.Refused
                ? t('model.order.status.Refused')
                : t('model.order.status.Accepted'),
        status: (activeStep >= 2 ? 'complete' : activeStep === 1 ? 'current' : 'upcoming') as 'complete' | 'current' | 'upcoming',
      },
    ]
  }

  return (
    <div className="m-5 sm:m-10 ">
      <div className="h-full flex flex-col items-center justify-center sm:px-6 md:px-8">
        <div className="w-full md:w-auto bg-white overflow-hidden rounded-lg shadow-sm">
          <div className="flex justify-between bg-primary text-white px-5 py-4 break-all">
            <p>{t('page.OrderTracking.orderReference', { referenceId })}</p>
            {(trackedOrder.status === OrderStatus.Canceled || trackedOrder.status === OrderStatus.Refused) && (
              <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                {t(`model.order.status.${trackedOrder.status}`)}
              </span>
            )}
          </div>
          <div className="md:border-b md:border-t md:border-gray-200">
            <nav className="mx-auto max-w-7xl" aria-label="Progress">
              <ol role="list" className="overflow-hidden md:flex md:border-l md:border-r md:border-gray-200">
                {returnSteps(trackedOrder).map((step) => (
                  <Step {...step} key={step.id} />
                ))}
              </ol>
            </nav>
          </div>
          <div className="overflow-hidden bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-base font-semibold leading-6 text-gray-900">{t('page.OrderTracking.orderInfo.title')}</h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                  <dt className="text-sm font-medium text-gray-500">{t('page.OrderTracking.orderInfo.field.patient.label')}</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {trackedOrder.createdBy.userInfo.name + ' ' + trackedOrder.createdBy.userInfo.surname}
                  </dd>
                </div>
                <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                  <dt className="text-sm font-medium text-gray-500">{t('page.OrderTracking.orderInfo.field.startDate.label')}</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{dayjs(trackedOrder.startDate).format('DD/MM/YYYY HH[h]mm')}</dd>
                </div>
                <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                  <dt className="text-sm font-medium text-gray-500">{t('page.OrderTracking.orderInfo.field.speciality.label')}</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{trackedOrder.speciality}</dd>
                </div>
                <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                  <dt className="text-sm font-medium text-gray-500">{t('page.OrderTracking.orderInfo.field.meetingPlace.label')}</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{trackedOrder.meetingPlace.description}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
